import React from "react";
import { Link } from "react-router-dom";
import { PageHero } from "../components/PageHero";
import { CTAStrip } from "../components/CTAStrip";
import { Palette, BookOpen, Music, Leaf, TreePine, Heart, Sparkles, FlaskConical } from "lucide-react";

const activities = [
  {
    icon: Palette,
    title: "Art & Craft",
    desc: "Children explore colours, shapes, and textures through painting, collage, and clay modelling. Every session sparks imagination and builds fine motor skills.",
    age: "Ages 2 – 6",
    tint: "bg-[#FFE1E1]",
    iconColor: "text-kid-orange",
    tag: "bg-kid-orange/10 text-kid-orange",
  },
  {
    icon: BookOpen,
    title: "Storytelling & Drama",
    desc: "Interactive read-alouds and role-play activities build vocabulary, listening skills, and a lifelong love of reading in a playful, low-pressure setting.",
    age: "Ages 3 – 6",
    tint: "bg-[#FFF0DB]",
    iconColor: "text-kid-amber",
    tag: "bg-kid-amber/10 text-kid-amber",
  },
  {
    icon: Music,
    title: "Music & Rhythm",
    desc: "Singing, clapping games, and simple instruments introduce children to rhythm and melody. Music lessons also strengthen memory and language development.",
    age: "Ages 2 – 6",
    tint: "bg-[#DCE7FF]",
    iconColor: "text-kid-softblue",
    tag: "bg-kid-softblue/20 text-kid-navy",
  },
  {
    icon: FlaskConical,
    title: "Science Explorers",
    desc: "Hands-on experiments — from mixing colours to watching plants grow — nurture curiosity and teach children to ask questions and observe the world around them.",
    age: "Ages 4 – 6",
    tint: "bg-[#E6F8E4]",
    iconColor: "text-emerald-500",
    tag: "bg-emerald-100 text-emerald-700",
  },
  {
    icon: TreePine,
    title: "Outdoor Play & Sports",
    desc: "Structured outdoor sessions with running, jumping, balancing, and team games build gross motor skills, confidence, and a healthy active lifestyle from an early age.",
    age: "Ages 2 – 6",
    tint: "bg-[#E6F8E4]",
    iconColor: "text-emerald-600",
    tag: "bg-emerald-100 text-emerald-700",
  },
  {
    icon: Heart,
    title: "Yoga & Mindfulness",
    desc: "Child-friendly yoga poses, breathing exercises, and guided relaxation help little ones develop body awareness, focus, and emotional regulation in a calm, joyful space.",
    age: "Ages 3 – 6",
    tint: "bg-[#FFE1E1]",
    iconColor: "text-kid-pink",
    tag: "bg-kid-pink/20 text-kid-navy",
  },
  {
    icon: Sparkles,
    title: "Cooking Adventures",
    desc: "Simple, no-heat recipes teach children about food, measurement, and following steps. Cooking together builds independence, maths readiness, and a sense of pride.",
    age: "Ages 4 – 6",
    tint: "bg-[#FFF0DB]",
    iconColor: "text-kid-amber",
    tag: "bg-kid-amber/10 text-kid-amber",
  },
  {
    icon: Leaf,
    title: "Nature & Gardening",
    desc: "Planting seeds, observing insects, and exploring our garden patch connect children with the natural world and develop patience, responsibility, and environmental care.",
    age: "Ages 3 – 6",
    tint: "bg-[#DCE7FF]",
    iconColor: "text-emerald-500",
    tag: "bg-emerald-100 text-emerald-700",
  },
];

const schedule = [
  { day: "Monday", activity: "Art & Craft", emoji: "🎨", bg: "bg-[#FFE1E1]" },
  { day: "Tuesday", activity: "Storytelling & Drama", emoji: "📖", bg: "bg-[#FFF0DB]" },
  { day: "Wednesday", activity: "Music & Rhythm", emoji: "🎵", bg: "bg-[#DCE7FF]" },
  { day: "Thursday", activity: "Science & Outdoor Play", emoji: "🔬", bg: "bg-[#E6F8E4]" },
  { day: "Friday", activity: "Yoga & Mindfulness", emoji: "🧘", bg: "bg-[#FFE1E1]" },
];

export default function Activities() {
  return (
    <div data-testid="page-activities">
      <PageHero title="Activities" breadcrumb="Activities" />

      {/* Activity cards */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl">
            <span className="section-label">What We Do</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold text-kid-navy">
              Learning Through Play & Discovery
            </h2>
            <p className="mt-4 text-kid-navy/70 text-lg leading-relaxed">
              Every activity at Spring Kids is thoughtfully designed to support children's physical, creative, and social development — while keeping things fun and age-appropriate.
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {activities.map((a) => (
              <div
                key={a.title}
                className="kid-card flex flex-col gap-4"
                data-testid={`activity-card-${a.title.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div className={`w-16 h-16 rounded-2xl ${a.tint} flex items-center justify-center flex-shrink-0`}>
                  <a.icon size={30} strokeWidth={2} className={a.iconColor} />
                </div>
                <div>
                  <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full ${a.tag}`}>
                    {a.age}
                  </span>
                  <h3 className="mt-2 text-lg font-bold text-kid-navy">{a.title}</h3>
                  <p className="mt-2 text-sm text-kid-navy/70 leading-relaxed">{a.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Weekly schedule */}
      <section className="bg-[#F3F4FC] py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="section-label">Weekly Rhythm</span>
            <h2 className="mt-2 text-3xl font-bold text-kid-navy">A Week at Spring Kids</h2>
            <p className="mt-3 text-kid-navy/70 max-w-xl mx-auto">
              Each day brings a fresh mix of structured learning and joyful exploration — so no two days feel the same.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {schedule.map((d) => (
              <div key={d.day} className={`rounded-3xl ${d.bg} p-6 text-center`} data-testid={`schedule-${d.day.toLowerCase()}`}>
                <div className="text-4xl">{d.emoji}</div>
                <p className="mt-3 font-bold text-kid-navy">{d.day}</p>
                <p className="mt-1 text-xs text-kid-navy/70 leading-snug">{d.activity}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* In-page CTA */}
      <section className="bg-white py-16">
        <div className="max-w-2xl mx-auto px-4 md:px-8 text-center">
          <div className="kid-card flex flex-col items-center gap-4">
            <div className="text-5xl">🌱</div>
            <h3 className="text-2xl font-bold text-kid-navy">Want to See It in Action?</h3>
            <p className="text-kid-navy/70 max-w-md">
              Book a free school visit and watch your child explore a real activity session alongside our teachers.
            </p>
            <Link to="/enquiry" className="btn-amber mt-2" data-testid="activities-book-visit-btn">
              Book a Free Visit
            </Link>
          </div>
        </div>
      </section>

      <CTAStrip />
    </div>
  );
}
